import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, X, Users, Landmark, Wallet, FileText, Search, Trash2, Pencil, Receipt, Coins, Gift, UserPlus, Mail, Upload, Download, CheckCircle2, AlertCircle, SkipForward } from 'lucide-react';
import { apiClient } from '../../services/api';
import { createUser, listDepartments, listJobTitles, bulkImportUsers } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const COUNTRIES = [
  { code: 'IN', name: 'India', ccy: 'INR' },
  { code: 'UK', name: 'United Kingdom', ccy: 'GBP' },
  { code: 'MY', name: 'Malaysia', ccy: 'MYR' },
  { code: 'GENERIC', name: 'Other', ccy: 'USD' },
];

const hr = {
  employees: () => apiClient.get('/hr/employees').then((r) => r.data),
  employee: (id) => apiClient.get(`/hr/employees/${id}`).then((r) => r.data),
  patchEmployee: (id, p) => apiClient.patch(`/hr/employees/${id}`, p).then((r) => r.data),
  components: () => apiClient.get('/hr/salary-components').then((r) => r.data),
  createComponent: (p) => apiClient.post('/hr/salary-components', p).then((r) => r.data),
  patchComponent: (id, p) => apiClient.patch(`/hr/salary-components/${id}`, p).then((r) => r.data),
  deleteComponent: (id) => apiClient.delete(`/hr/salary-components/${id}`).then((r) => r.data),
  getStructure: (id) => apiClient.get(`/hr/employees/${id}/salary-structure`).then((r) => r.data),
  putStructure: (id, p) => apiClient.put(`/hr/employees/${id}/salary-structure`, p).then((r) => r.data),
  leaveTypes: () => apiClient.get('/hr/leave-types').then((r) => r.data),
  createLeaveType: (p) => apiClient.post('/hr/leave-types', p).then((r) => r.data),
  deleteLeaveType: (id) => apiClient.delete(`/hr/leave-types/${id}`).then((r) => r.data),
  leaveRequests: (q = '') => apiClient.get(`/hr/leave-requests${q}`).then((r) => r.data),
  createLeaveRequest: (p) => apiClient.post('/hr/leave-requests', p).then((r) => r.data),
  decideLeaveRequest: (id, p) => apiClient.patch(`/hr/leave-requests/${id}`, p).then((r) => r.data),
  seedDefaults: () => apiClient.post('/hr/seed-defaults').then((r) => r.data),
  // masters
  reimbCats: () => apiClient.get('/hr/masters/reimb-categories').then((r) => r.data),
  createReimbCat: (p) => apiClient.post('/hr/masters/reimb-categories', p).then((r) => r.data),
  patchReimbCat: (id, p) => apiClient.patch(`/hr/masters/reimb-categories/${id}`, p).then((r) => r.data),
  deleteReimbCat: (id) => apiClient.delete(`/hr/masters/reimb-categories/${id}`).then((r) => r.data),
  loanTypes: () => apiClient.get('/hr/masters/loan-types').then((r) => r.data),
  createLoanType: (p) => apiClient.post('/hr/masters/loan-types', p).then((r) => r.data),
  patchLoanType: (id, p) => apiClient.patch(`/hr/masters/loan-types/${id}`, p).then((r) => r.data),
  deleteLoanType: (id) => apiClient.delete(`/hr/masters/loan-types/${id}`).then((r) => r.data),
  earningHeads: () => apiClient.get('/hr/masters/earning-heads').then((r) => r.data),
  createEarningHead: (p) => apiClient.post('/hr/masters/earning-heads', p).then((r) => r.data),
  patchEarningHead: (id, p) => apiClient.patch(`/hr/masters/earning-heads/${id}`, p).then((r) => r.data),
  deleteEarningHead: (id) => apiClient.delete(`/hr/masters/earning-heads/${id}`).then((r) => r.data),
  seedMasters: () => apiClient.post('/hr/masters/seed-defaults').then((r) => r.data),
};

const HRPage = () => {
  const [tab, setTab] = useState('employees');
  const authCtx = useAuth();
  const role = authCtx?.user?.role;
  const fullAccess = role === 'super_admin' || role === 'admin';
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4 sm:mb-6">
        <div className="min-w-0">
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Human Resources</span>
          <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            HR Workspace
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Employees · salary masters · leave · claims.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
        <button onClick={async () => { try { const r = await hr.seedDefaults(); toast({ title: 'Seeded defaults', description: `${r.components} components · ${r.leave_types} leave types` }); } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); } }}
          data-testid="hr-seed-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8]">
          Seed defaults
        </button>
        <button onClick={async () => { try { const r = await hr.seedMasters(); toast({ title: 'Seeded masters', description: `${r.reimb_categories} reimb + ${r.loan_types} loans + ${r.earning_heads} heads` }); } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); } }}
          data-testid="hr-seed-masters-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8]">
          Seed masters
        </button>
        </div>
      </div>
      <div className="flex border-b border-black/5 bg-[#f7fbf8] rounded-t-2xl overflow-x-auto mb-4">
        {[
          ['employees', <Users key="i" className="w-4 h-4" />, 'Employees'],
          ...(fullAccess ? [['components', <Wallet key="i" className="w-4 h-4" />, 'Salary components']] : []),
          ...(fullAccess ? [['earning-heads', <Gift key="i" className="w-4 h-4" />, 'Earning heads']] : []),
          ['reimb-cats', <Receipt key="i" className="w-4 h-4" />, 'Reimbursement cats'],
          ['loan-types', <Coins key="i" className="w-4 h-4" />, 'Loan types'],
          ['leave-types', <FileText key="i" className="w-4 h-4" />, 'Leave types'],
          ['leave-requests', <Landmark key="i" className="w-4 h-4" />, 'Leave requests'],
        ].map(([k, icon, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`hr-tab-${k}`}
            className={`px-3 sm:px-4 py-2.5 text-sm font-medium inline-flex items-center gap-1.5 whitespace-nowrap ${tab === k ? 'text-[#0f5c3d] bg-white border-b-2 border-[#0f5c3d]' : 'text-[#0b1a2e]/60 hover:text-[#0b1a2e]'}`}>
            {icon} {label}
          </button>
        ))}
      </div>
      {tab === 'employees' && <EmployeesTab />}
      {tab === 'components' && fullAccess && <ComponentsTab />}
      {tab === 'earning-heads' && fullAccess && <EarningHeadsTab />}
      {tab === 'reimb-cats' && <ReimbCategoriesTab />}
      {tab === 'loan-types' && <LoanTypesTab />}
      {tab === 'leave-types' && <LeaveTypesTab />}
      {tab === 'leave-requests' && <LeaveRequestsTab />}
    </div>
  );
};

const EmployeesTab = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const authCtx = useAuth();
  const canInvite = authCtx?.user?.role === 'super_admin';
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await hr.employees(); setEmployees(r.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const filtered = employees.filter((e) => !q || (e.name || e.email || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div className={`lg:col-span-5 bg-white border border-black/5 rounded-2xl overflow-hidden ${selectedId ? 'hidden lg:block' : ''}`}>
        <div className="p-3 border-b border-black/5 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees…" data-testid="employee-search"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#0f5c3d]/15 text-sm" />
            </div>
            {canInvite && (
              <>
                <button
                  onClick={() => setShowBulk(true)}
                  data-testid="bulk-import-btn"
                  title="Upload a CSV to onboard many employees at once"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/25 text-[#0f5c3d] text-xs font-semibold hover:bg-[#f7fbf8] whitespace-nowrap"
                >
                  <Upload className="w-3.5 h-3.5" /> Import
                </button>
                <button
                  onClick={() => setShowAdd(true)}
                  data-testid="add-employee-btn"
                  title="Create a new team member (login + HR profile)"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold hover:bg-[#0b4a30] whitespace-nowrap"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Add
                </button>
              </>
            )}
          </div>
          <div className="text-[11px] text-[#0b1a2e]/50">{filtered.length} of {employees.length} employees</div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {loading ? <div className="p-8 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 inline animate-spin" /></div> :
            filtered.map((e) => (
              <button key={e.user_id} onClick={() => setSelectedId(e.user_id)} data-testid={`employee-row-${e.user_id}`}
                className={`w-full text-left p-3 border-b border-black/5 hover:bg-[#f7fbf8] ${selectedId === e.user_id ? 'bg-[#f7fbf8]' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold flex items-center justify-center">{(e.name || e.email || '?').charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0b1a2e] truncate">{e.name || e.email}</div>
                    <div className="text-[11px] text-[#0b1a2e]/50 truncate">{[e.employee_code, e.designation, e.country].filter(Boolean).join(' · ') || 'Not set up'}</div>
                  </div>
                </div>
              </button>
            ))}
        </div>
      </div>
      <div className={`lg:col-span-7 ${selectedId ? 'block' : 'hidden lg:block'}`}>
        {selectedId ? <EmployeeDetail id={selectedId} onBack={() => setSelectedId(null)} onSaved={load} /> :
          <div className="bg-white border border-black/5 rounded-2xl p-16 text-center text-[#0b1a2e]/50">Select an employee to edit their HR profile.</div>}
      </div>
      {showAdd && <AddEmployeeModal onClose={() => setShowAdd(false)} onCreated={(newId) => { setShowAdd(false); load(); if (newId) setSelectedId(newId); }} />}
      {showBulk && <BulkImportModal onClose={() => setShowBulk(false)} onDone={() => { load(); }} />}
    </div>
  );
};

/**
 * Bulk CSV import modal. Uploads to POST /users/bulk-import (super_admin only).
 * Displays a per-row result table (created / skipped / error).
 */
const BulkImportModal = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaderCsv = async () => {
    // Grab template through axios so it picks up the JWT automatically.
    try {
      const res = await apiClient.get('/users/bulk-import-template.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'emerald_employees_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Template download failed', description: e?.response?.data?.detail || 'Please retry.', variant: 'destructive' });
    }
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const data = await bulkImportUsers(file);
      setResult(data);
      const { created = 0, skipped = 0, errors = 0 } = data.summary || {};
      toast({
        title: `Import finished — ${created} created`,
        description: `${skipped} skipped · ${errors} error${errors === 1 ? '' : 's'}`,
      });
      if (created > 0) onDone?.();
    } catch (e) {
      toast({
        title: 'Import failed',
        description: e?.response?.data?.detail || 'Please check the CSV format.',
        variant: 'destructive',
      });
    } finally { setUploading(false); }
  };

  const badge = (status) => {
    if (status === 'created') return <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px] font-semibold"><CheckCircle2 className="w-3 h-3" /> Created</span>;
    if (status === 'skipped') return <span className="inline-flex items-center gap-1 text-amber-700 text-[11px] font-semibold"><SkipForward className="w-3 h-3" /> Skipped</span>;
    return <span className="inline-flex items-center gap-1 text-red-600 text-[11px] font-semibold"><AlertCircle className="w-3 h-3" /> Error</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bulk-import-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between bg-[#0b1a2e] text-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7bbf95] font-semibold">Human Resources</div>
            <div className="text-lg font-semibold flex items-center gap-2"><Upload className="w-5 h-5" /> Bulk import employees</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {!result && (
            <>
              <div className="bg-[#f7fbf8] border border-[#0f5c3d]/15 rounded-lg p-4 space-y-2">
                <div className="text-sm font-semibold text-[#0b1a2e]">CSV format</div>
                <div className="text-xs text-[#0b1a2e]/70 leading-relaxed">
                  Header row is required. Columns (case-insensitive):
                  <div className="mt-1 font-mono text-[11px] text-[#0f5c3d] break-all">
                    name, email, role, department, job_title, send_invite, country, hire_date, employee_code, designation, contract_type
                  </div>
                  <ul className="mt-2 space-y-0.5 list-disc list-inside">
                    <li><b>name</b> and <b>email</b> are required. Existing emails are skipped, not overwritten.</li>
                    <li><b>role</b>: staff (default) · admin · super_admin · hr · accounts · content_publisher</li>
                    <li><b>department</b> / <b>job_title</b>: matched by exact name against existing masters</li>
                    <li><b>country</b>: IN, UK, MY or GENERIC. Currency auto-fills.</li>
                    <li><b>send_invite</b>: true (default) sends a 48-hour set-password email</li>
                  </ul>
                </div>
                <button
                  onClick={authHeaderCsv}
                  data-testid="bulk-template-download"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0f5c3d]/25 text-[#0f5c3d] text-xs font-semibold hover:bg-white"
                >
                  <Download className="w-3.5 h-3.5" /> Download sample template
                </button>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">
                  Choose CSV file (max 2 MB)
                </label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="bulk-import-file"
                  className="w-full text-sm border border-slate-300 rounded p-2"
                />
                {file && (
                  <div className="mt-1 text-[11px] text-[#0b1a2e]/60">
                    Ready: <b>{file.name}</b> · {Math.round(file.size / 1024)} KB
                  </div>
                )}
              </div>
            </>
          )}

          {result && (
            <div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <SummaryTile color="bg-emerald-50 text-emerald-800 border-emerald-200" icon={CheckCircle2} label="Created" value={result.summary?.created || 0} />
                <SummaryTile color="bg-amber-50 text-amber-800 border-amber-200" icon={SkipForward} label="Skipped" value={result.summary?.skipped || 0} />
                <SummaryTile color="bg-red-50 text-red-700 border-red-200" icon={AlertCircle} label="Errors" value={result.summary?.errors || 0} />
              </div>
              <div className="border border-black/5 rounded-lg overflow-hidden max-h-[45vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                    <tr>
                      <th className="text-left p-2">Row</th>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.results || []).map((r) => (
                      <tr key={r.row} className="border-t border-black/5" data-testid={`bulk-row-${r.row}`}>
                        <td className="p-2 text-xs font-mono text-[#0b1a2e]/50">{r.row}</td>
                        <td className="p-2 text-xs">{r.email || '—'}</td>
                        <td className="p-2">{badge(r.status)}</td>
                        <td className="p-2 text-xs text-[#0b1a2e]/70">
                          {r.status === 'created' && (r.invited ? `invited as ${r.role}` : `created as ${r.role}`)}
                          {r.status === 'skipped' && r.reason}
                          {r.status === 'error' && r.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={submit}
              disabled={!file || uploading}
              data-testid="bulk-import-submit"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-60 hover:bg-[#0b4a30]"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import CSV
            </button>
          )}
          {result && (
            <button
              type="button"
              onClick={() => { setResult(null); setFile(null); }}
              data-testid="bulk-import-another"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0b1a2e] text-white text-sm font-semibold hover:bg-[#0f5c3d]"
            >
              <Upload className="w-4 h-4" /> Import another
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryTile = ({ color, icon: Icon, label, value }) => (
  <div className={`rounded-lg border p-3 ${color}`}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

/**
 * One-shot modal to create a new employee: creates the login user account
 * (POST /users) and immediately fills in the HR profile
 * (PATCH /hr/employees/{user_id}). Super admin only.
 */
const AddEmployeeModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    // user account
    name: '', email: '', role: 'staff', department_id: '', job_title_id: '', send_invite: true,
    // HR profile
    country: 'IN', hire_date: new Date().toISOString().slice(0, 10),
    employee_code: '', designation: '', contract_type: 'permanent',
  });
  const [depts, setDepts] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('user'); // 'user' | 'hr'

  useEffect(() => {
    Promise.all([
      listDepartments().catch(() => ({ data: [] })),
      listJobTitles().catch(() => ({ data: [] })),
    ]).then(([d, j]) => { setDepts(d.data || []); setJobTitles(j.data || []); });
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim()) {
      toast({ title: 'Name and email are required', variant: 'destructive' });
      setStep('user');
      return;
    }
    setSaving(true);
    try {
      // 1) create the user account (invite email fires in background if send_invite=true)
      const newUser = await createUser({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        department_id: form.department_id || null,
        job_title_id: form.job_title_id || null,
        send_invite: form.send_invite,
      });

      // 2) fill in the HR profile so they immediately show up in Attendance / Payroll
      const hrPatch = {};
      if (form.country) hrPatch.country = form.country;
      if (form.hire_date) hrPatch.hire_date = form.hire_date;
      if (form.employee_code) hrPatch.employee_code = form.employee_code.trim();
      if (form.designation) hrPatch.designation = form.designation.trim();
      if (form.contract_type) hrPatch.contract_type = form.contract_type;

      try {
        if (Object.keys(hrPatch).length > 0) {
          await apiClient.patch(`/hr/employees/${newUser.user_id}`, hrPatch);
        }
      } catch (hrErr) {
        // User already created — surface HR issue but don't block
        toast({
          title: 'User created, HR profile partial',
          description: hrErr?.response?.data?.detail || 'You can complete the HR profile below.',
          variant: 'destructive',
        });
      }

      toast({
        title: 'Employee added',
        description: form.send_invite
          ? `Invite email sent to ${form.email}. They'll set their password via the link.`
          : `${form.name} created. Share their login separately.`,
      });
      onCreated?.(newUser.user_id);
    } catch (err) {
      toast({
        title: 'Failed to add employee',
        description: err?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-employee-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between bg-[#0b1a2e] text-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7bbf95] font-semibold">Human Resources</div>
            <div className="text-lg font-semibold flex items-center gap-2"><UserPlus className="w-5 h-5" /> Add new employee</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex border-b border-black/5 bg-[#f7fbf8]">
          {[
            ['user', 'Account & role'],
            ['hr', 'HR profile'],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setStep(k)}
              data-testid={`add-emp-step-${k}`}
              className={`px-5 py-2.5 text-sm font-medium ${step === k ? 'text-[#0f5c3d] bg-white border-b-2 border-[#0f5c3d]' : 'text-[#0b1a2e]/60'}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {step === 'user' ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Full name *">
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-emp-name" placeholder="Priya Sharma" />
                </FormField>
                <FormField label="Work email *">
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-emp-email" placeholder="priya@emeraldcorporates.com" />
                </FormField>
                <FormField label="Role">
                  <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="add-emp-role">
                    <option value="staff">Staff</option>
                    <option value="hr">HR</option>
                    <option value="accounts">Accounts</option>
                    <option value="content_publisher">Content Publisher</option>
                    <option value="admin">Admin</option>
                  </Select>
                </FormField>
                <FormField label="Department">
                  <Select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} data-testid="add-emp-dept">
                    <option value="">— none —</option>
                    {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </FormField>
                <FormField label="Job title">
                  <Select value={form.job_title_id} onChange={(e) => setForm({ ...form, job_title_id: e.target.value })} data-testid="add-emp-jt">
                    <option value="">— none —</option>
                    {jobTitles.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
                  </Select>
                </FormField>
              </div>

              <label className="flex items-start gap-2 text-sm bg-[#f7fbf8] p-3 rounded-lg cursor-pointer">
                <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} className="accent-[#0f5c3d] mt-1" data-testid="add-emp-send-invite" />
                <div>
                  <div className="font-medium text-[#0b1a2e] flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Send invitation email</div>
                  <div className="text-xs text-[#0b1a2e]/60 mt-0.5">A 48-hour set-password link will be emailed. Uncheck if you want to share credentials manually.</div>
                </div>
              </label>
            </>
          ) : (
            <>
              <div className="text-xs text-[#0b1a2e]/60 mb-1">Fill in the basics now — bank details, PAN, salary structure can be set on the profile page after creation.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Country">
                  <Select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="add-emp-country">
                    {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </Select>
                </FormField>
                <FormField label="Hire date">
                  <Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="add-emp-hire-date" />
                </FormField>
                <FormField label="Employee code">
                  <Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} data-testid="add-emp-code" placeholder="EMP-024" />
                </FormField>
                <FormField label="Designation">
                  <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="add-emp-designation" placeholder="Senior Analyst" />
                </FormField>
                <FormField label="Contract type">
                  <Select value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} data-testid="add-emp-contract">
                    {['permanent', 'contract', 'intern', 'consultant'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </FormField>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                <b>Tip:</b> once created, open the employee to configure salary structure, bank details and manager. They&apos;ll then show up in Attendance, Payroll and Team Dashboard automatically.
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex items-center justify-between gap-2">
          <div className="text-[11px] text-[#0b1a2e]/50">
            {step === 'user' ? 'Step 1 of 2 · account details' : 'Step 2 of 2 · HR profile'}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Cancel</button>
            {step === 'user' ? (
              <button type="button" onClick={() => setStep('hr')} data-testid="add-emp-next-btn" className="px-5 py-2 rounded-lg bg-[#0b1a2e] text-white text-sm font-medium hover:bg-[#0f5c3d]">Next</button>
            ) : (
              <>
                <button type="button" onClick={() => setStep('user')} className="px-4 py-2 rounded-lg text-sm border border-[#0f5c3d]/20 hover:bg-white">Back</button>
                <button type="submit" disabled={saving} data-testid="add-emp-submit-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-60 hover:bg-[#0b4a30]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Create employee
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

const FormField = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>
    {children}
  </div>
);
const Input = (props) => <input {...props} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${props.className || ''}`} />;
const Select = (props) => <select {...props} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white ${props.className || ''}`}>{props.children}</select>;

const EmployeeDetail = ({ id, onBack, onSaved }) => {
  const [emp, setEmp] = useState(null);
  const [components, setComponents] = useState([]);
  const [structure, setStructure] = useState({ lines: [], ctc_annual: null });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([hr.employee(id), hr.components(), hr.getStructure(id)]).then(([e, c, s]) => {
      setEmp(e); setComponents(c.data || []); setStructure(s || { lines: [] });
    });
  }, [id]);
  if (!emp) return <div className="bg-white border border-black/5 rounded-2xl p-8 text-center"><Loader2 className="w-5 h-5 inline animate-spin" /></div>;
  const applicableComponents = components.filter((c) => c.country === 'ALL' || c.country === emp.country);
  const save = async () => {
    setSaving(true);
    try {
      await hr.patchEmployee(id, {
        country: emp.country, currency: emp.currency, hire_date: emp.hire_date, employee_code: emp.employee_code,
        designation: emp.designation, contract_type: emp.contract_type, pan_or_nino: emp.pan_or_nino,
        bank_account_name: emp.bank_account_name, bank_account_number: emp.bank_account_number,
        bank_ifsc_or_sort: emp.bank_ifsc_or_sort, bank_name: emp.bank_name, date_of_birth: emp.date_of_birth,
        address: emp.address, emergency_contact: emp.emergency_contact,
      });
      await hr.putStructure(id, { ctc_annual: structure.ctc_annual, lines: structure.lines.filter((l) => l.component_id && l.amount) });
      toast({ title: 'Saved' });
      onSaved?.();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  const totalEarnings = structure.lines.filter((l) => applicableComponents.find((c) => c.id === l.component_id)?.kind === 'earning').reduce((s, l) => s + Number(l.amount || 0), 0);
  const totalDeductions = structure.lines.filter((l) => applicableComponents.find((c) => c.id === l.component_id)?.kind === 'deduction').reduce((s, l) => s + Number(l.amount || 0), 0);
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-4 sm:p-6">
        <button onClick={onBack} className="lg:hidden mb-2 text-xs text-[#0f5c3d] hover:underline">← Back</button>
        <div className="text-lg font-semibold text-[#0b1a2e]">{emp.name || emp.email}</div>
        <div className="text-xs text-[#0b1a2e]/60">{emp.email}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <FormField label="Employee code"><Input value={emp.employee_code || ''} onChange={(e) => setEmp({ ...emp, employee_code: e.target.value })} data-testid="emp-code" /></FormField>
          <FormField label="Designation"><Input value={emp.designation || ''} onChange={(e) => setEmp({ ...emp, designation: e.target.value })} /></FormField>
          <FormField label="Country">
            <Select value={emp.country || ''} onChange={(e) => setEmp({ ...emp, country: e.target.value, currency: COUNTRIES.find((c) => c.code === e.target.value)?.ccy })} data-testid="emp-country">
              <option value="">Select country</option>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </FormField>
          <FormField label="Currency"><Input value={emp.currency || ''} onChange={(e) => setEmp({ ...emp, currency: e.target.value })} /></FormField>
          <FormField label="Hire date"><Input type="date" value={emp.hire_date?.slice(0, 10) || ''} onChange={(e) => setEmp({ ...emp, hire_date: e.target.value })} /></FormField>
          <FormField label="Contract type">
            <Select value={emp.contract_type || ''} onChange={(e) => setEmp({ ...emp, contract_type: e.target.value })}>
              <option value="">—</option>
              {['permanent', 'contract', 'intern', 'consultant'].map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </FormField>
          <FormField label="Tax ID (PAN / NINO / NRIC)"><Input value={emp.pan_or_nino || ''} onChange={(e) => setEmp({ ...emp, pan_or_nino: e.target.value })} /></FormField>
          <FormField label="Date of birth"><Input type="date" value={emp.date_of_birth?.slice(0, 10) || ''} onChange={(e) => setEmp({ ...emp, date_of_birth: e.target.value })} /></FormField>
          <FormField label="Bank name"><Input value={emp.bank_name || ''} onChange={(e) => setEmp({ ...emp, bank_name: e.target.value })} /></FormField>
          <FormField label="Bank account #"><Input value={emp.bank_account_number || ''} onChange={(e) => setEmp({ ...emp, bank_account_number: e.target.value })} /></FormField>
          <FormField label="IFSC / Sort code"><Input value={emp.bank_ifsc_or_sort || ''} onChange={(e) => setEmp({ ...emp, bank_ifsc_or_sort: e.target.value })} /></FormField>
          <FormField label="Emergency contact"><Input value={emp.emergency_contact || ''} onChange={(e) => setEmp({ ...emp, emergency_contact: e.target.value })} /></FormField>
        </div>

        <h3 className="mt-6 text-sm font-semibold text-[#0f5c3d] uppercase tracking-widest">Salary structure</h3>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="CTC annual"><Input type="number" value={structure.ctc_annual || ''} onChange={(e) => setStructure({ ...structure, ctc_annual: parseFloat(e.target.value || 0) })} /></FormField>
        </div>
        <div className="mt-3 space-y-2">
          {structure.lines.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <Select value={l.component_id || ''} onChange={(e) => { const lines = [...structure.lines]; lines[i] = { ...l, component_id: e.target.value }; setStructure({ ...structure, lines }); }} className="flex-1" data-testid={`salary-line-comp-${i}`}>
                <option value="">Select component</option>
                {applicableComponents.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.kind})</option>)}
              </Select>
              <Input type="number" value={l.amount || ''} onChange={(e) => { const lines = [...structure.lines]; lines[i] = { ...l, amount: parseFloat(e.target.value || 0) }; setStructure({ ...structure, lines }); }} className="w-32" data-testid={`salary-line-amt-${i}`} />
              <button onClick={() => { const lines = structure.lines.filter((_, ix) => ix !== i); setStructure({ ...structure, lines }); }} className="text-red-600 p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setStructure({ ...structure, lines: [...structure.lines, { component_id: '', amount: 0 }] })} data-testid="add-salary-line"
            className="text-xs text-[#0f5c3d] hover:underline">+ Add component</button>
        </div>
        <div className="mt-3 flex justify-between text-xs bg-[#f7fbf8] p-2 rounded">
          <span>Gross earnings: <b>{totalEarnings.toFixed(2)}</b></span>
          <span>Deductions: <b>{totalDeductions.toFixed(2)}</b></span>
          <span>Net: <b className="text-[#0f5c3d]">{(totalEarnings - totalDeductions).toFixed(2)}</b> {emp.currency}</span>
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={save} disabled={saving} data-testid="save-employee-btn"
            className="px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">
            {saving && <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

const ComponentsTab = () => {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { const r = await hr.components(); setItems(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm font-medium">{items.length} components</div>
        <button onClick={() => setShowNew(true)} data-testid="new-component-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Code</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Name</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Kind</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Country</th>
          <th className="p-2"></th>
        </tr></thead>
        <tbody>{items.map((c) => (
          <tr key={c.id} className="border-t border-black/5" data-testid={`component-row-${c.code}`}>
            <td className="p-2 font-mono text-xs">{c.code}</td>
            <td className="p-2">{c.name}</td>
            <td className="p-2"><span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${c.kind === 'earning' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{c.kind}</span></td>
            <td className="p-2 text-xs">{c.country}</td>
            <td className="p-2 text-right">
              <button onClick={async () => { if (window.confirm(`Delete ${c.name}?`)) { await hr.deleteComponent(c.id); load(); } }} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {showNew && <ComponentModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const ComponentModal = ({ onClose, onSaved }) => {
  const [f, setF] = useState({ code: '', name: '', kind: 'earning', country: 'ALL', taxable: true, statutory: false, display_order: 100 });
  const save = async () => {
    try { await hr.createComponent({ ...f, code: f.code.toUpperCase() }); toast({ title: 'Component created' }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="new-component-modal">
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New salary component</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <FormField label="Code (uppercase, letters/numbers/_)"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="new-comp-code" /></FormField>
          <FormField label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormField>
          <FormField label="Kind">
            <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="earning">Earning</option><option value="deduction">Deduction</option>
            </Select>
          </FormField>
          <FormField label="Country">
            <Select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })}>
              <option value="ALL">All countries</option>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </FormField>
          <button onClick={save} data-testid="new-comp-save" className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">Create</button>
        </div>
      </div>
    </div>
  );
};

const LeaveTypesTab = () => {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { const r = await hr.leaveTypes(); setItems(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm">{items.length} leave types</div>
        <button onClick={() => setShowNew(true)} data-testid="new-leave-type-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Code</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Name</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Days/yr</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Country</th>
          <th className="p-2"></th>
        </tr></thead>
        <tbody>{items.map((t) => (
          <tr key={t.id} className="border-t border-black/5">
            <td className="p-2 font-mono text-xs">{t.code}</td>
            <td className="p-2">{t.name}</td>
            <td className="p-2 text-right">{t.days_per_year}{t.carry_forward ? ' (CF)' : ''}</td>
            <td className="p-2 text-xs">{t.country}</td>
            <td className="p-2 text-right"><button onClick={async () => { if (window.confirm(`Delete ${t.name}?`)) { await hr.deleteLeaveType(t.id); load(); } }} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
          </tr>
        ))}</tbody>
      </table>
      {showNew && <LeaveTypeModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const LeaveTypeModal = ({ onClose, onSaved }) => {
  const [f, setF] = useState({ code: '', name: '', days_per_year: 12, carry_forward: false, country: 'ALL', paid: true });
  const save = async () => {
    try { await hr.createLeaveType({ ...f, code: f.code.toUpperCase() }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New leave type</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <FormField label="Code"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></FormField>
          <FormField label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormField>
          <FormField label="Days per year"><Input type="number" value={f.days_per_year} onChange={(e) => setF({ ...f, days_per_year: parseFloat(e.target.value || 0) })} /></FormField>
          <FormField label="Country">
            <Select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })}>
              <option value="ALL">All</option>{COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </FormField>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.carry_forward} onChange={(e) => setF({ ...f, carry_forward: e.target.checked })} /> Carry forward</label>
          <button onClick={save} className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">Create</button>
        </div>
      </div>
    </div>
  );
};

const LeaveRequestsTab = () => {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => {
    const [r, t] = await Promise.all([hr.leaveRequests(), hr.leaveTypes()]);
    setItems(r.data || []); setTypes(t.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const typeById = Object.fromEntries(types.map((t) => [t.id, t]));
  const decide = async (r, decision) => {
    try { await hr.decideLeaveRequest(r.id, { decision }); toast({ title: `${decision}` }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm">{items.length} requests</div>
        <button onClick={() => setShowNew(true)} data-testid="new-leave-request-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> Request leave</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">User</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Type</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">From</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">To</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Days</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Status</th>
          <th></th>
        </tr></thead>
        <tbody>{items.map((r) => (
          <tr key={r.id} className="border-t border-black/5">
            <td className="p-2">{r.user_name || r.user_id}</td>
            <td className="p-2 text-xs">{typeById[r.leave_type_id]?.name || r.leave_type_id}</td>
            <td className="p-2 text-xs">{r.from_date}</td>
            <td className="p-2 text-xs">{r.to_date}</td>
            <td className="p-2 text-right">{r.days}</td>
            <td className="p-2"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.status === 'pending' ? 'bg-amber-100 text-amber-800' : r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{r.status}</span></td>
            <td className="p-2 text-right">
              {r.status === 'pending' && (
                <div className="inline-flex gap-1">
                  <button onClick={() => decide(r, 'approved')} data-testid={`leave-approve-${r.id}`} className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px]">Approve</button>
                  <button onClick={() => decide(r, 'rejected')} data-testid={`leave-reject-${r.id}`} className="px-2 py-0.5 rounded bg-red-600 text-white text-[10px]">Reject</button>
                </div>
              )}
            </td>
          </tr>
        ))}</tbody>
      </table>
      {showNew && <LeaveRequestModal types={types} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const LeaveRequestModal = ({ types, onClose, onSaved }) => {
  const [f, setF] = useState({ leave_type_id: '', from_date: '', to_date: '', reason: '' });
  const save = async () => {
    try { await hr.createLeaveRequest(f); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New leave request</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <FormField label="Type">
            <Select value={f.leave_type_id} onChange={(e) => setF({ ...f, leave_type_id: e.target.value })}>
              <option value="">Select</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </FormField>
          <FormField label="From"><Input type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} /></FormField>
          <FormField label="To"><Input type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} /></FormField>
          <FormField label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></FormField>
          <button onClick={save} data-testid="submit-leave-request" className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">Submit</button>
        </div>
      </div>
    </div>
  );
};

// ---- Reimbursement Categories master ----
const ReimbCategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(null);
  const load = useCallback(async () => { const r = await hr.reimbCats(); setItems(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => { if (window.confirm(`Delete ${c.name}?`)) { await hr.deleteReimbCat(c.id); load(); } };
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm">{items.length} categories</div>
        <button onClick={() => setShowEdit({})} data-testid="new-reimb-cat-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Code</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Name</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Taxable</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">GL account</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Max</th>
          <th></th>
        </tr></thead>
        <tbody>{items.map((c) => (
          <tr key={c.id} className="border-t border-black/5" data-testid={`reimb-cat-row-${c.code}`}>
            <td className="p-2 font-mono text-xs">{c.code}</td>
            <td className="p-2">{c.name}</td>
            <td className="p-2 text-xs">{c.taxable ? 'Yes' : 'No'}</td>
            <td className="p-2 text-xs">{c.gl_account_code || '-'}</td>
            <td className="p-2 text-right text-xs">{c.max_amount || '-'}</td>
            <td className="p-2 text-right">
              <button onClick={() => setShowEdit(c)} className="text-[#0f5c3d] text-xs mr-2"><Pencil className="w-3.5 h-3.5 inline" /></button>
              <button onClick={() => del(c)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No categories — click "Seed masters" up top for defaults.</div>}
      {showEdit && <MasterModal title="Reimbursement Category" initial={showEdit}
        fields={[['code', 'Code (A-Z_)'], ['name', 'Name'], ['gl_account_code', 'GL account code'], ['max_amount', 'Max amount', 'number']]}
        toggles={[['taxable', 'Taxable'], ['active', 'Active', true]]}
        onSave={async (p) => { p.code = (p.code || '').toUpperCase(); if (p.id) await hr.patchReimbCat(p.id, p); else await hr.createReimbCat(p); }}
        onClose={() => setShowEdit(null)} onSaved={() => { setShowEdit(null); load(); }} />}
    </div>
  );
};

// ---- Loan Types master ----
const LoanTypesTab = () => {
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(null);
  const load = useCallback(async () => { const r = await hr.loanTypes(); setItems(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => { if (window.confirm(`Delete ${c.name}?`)) { await hr.deleteLoanType(c.id); load(); } };
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm">{items.length} loan types</div>
        <button onClick={() => setShowEdit({})} data-testid="new-loan-type-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Code</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Name</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Interest%</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Max EMIs</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Max principal</th>
          <th></th>
        </tr></thead>
        <tbody>{items.map((c) => (
          <tr key={c.id} className="border-t border-black/5" data-testid={`loan-type-row-${c.code}`}>
            <td className="p-2 font-mono text-xs">{c.code}</td>
            <td className="p-2">{c.name}</td>
            <td className="p-2 text-right text-xs">{c.default_interest_pct}%</td>
            <td className="p-2 text-right text-xs">{c.max_emi_months}</td>
            <td className="p-2 text-right text-xs">{c.max_principal || '-'}</td>
            <td className="p-2 text-right">
              <button onClick={() => setShowEdit(c)} className="text-[#0f5c3d] text-xs mr-2"><Pencil className="w-3.5 h-3.5 inline" /></button>
              <button onClick={() => del(c)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No loan types — click "Seed masters" up top for defaults.</div>}
      {showEdit && <MasterModal title="Loan Type" initial={showEdit}
        fields={[['code', 'Code (A-Z_)'], ['name', 'Name'], ['default_interest_pct', 'Default interest %', 'number'], ['max_emi_months', 'Max EMI months', 'number'], ['max_principal', 'Max principal', 'number']]}
        toggles={[['active', 'Active', true]]}
        onSave={async (p) => { p.code = (p.code || '').toUpperCase(); if (p.id) await hr.patchLoanType(p.id, p); else await hr.createLoanType(p); }}
        onClose={() => setShowEdit(null)} onSaved={() => { setShowEdit(null); load(); }} />}
    </div>
  );
};

// ---- Earning Heads master ----
const EarningHeadsTab = () => {
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(null);
  const load = useCallback(async () => { const r = await hr.earningHeads(); setItems(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => { if (window.confirm(`Delete ${c.name}?`)) { await hr.deleteEarningHead(c.id); load(); } };
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm">{items.length} earning heads</div>
        <button onClick={() => setShowEdit({})} data-testid="new-earning-head-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Code</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Name</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Kind</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Taxable</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">One-time</th>
          <th></th>
        </tr></thead>
        <tbody>{items.map((c) => (
          <tr key={c.id} className="border-t border-black/5" data-testid={`earning-head-row-${c.code}`}>
            <td className="p-2 font-mono text-xs">{c.code}</td>
            <td className="p-2">{c.name}</td>
            <td className="p-2 text-xs"><span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{c.kind}</span></td>
            <td className="p-2 text-xs">{c.taxable ? 'Yes' : 'No'}</td>
            <td className="p-2 text-xs">{c.one_time ? 'Yes' : 'No'}</td>
            <td className="p-2 text-right">
              <button onClick={() => setShowEdit(c)} className="text-[#0f5c3d] text-xs mr-2"><Pencil className="w-3.5 h-3.5 inline" /></button>
              <button onClick={() => del(c)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No earning heads — click "Seed masters" up top for defaults.</div>}
      {showEdit && <MasterModal title="Earning Head" initial={showEdit}
        fields={[['code', 'Code (A-Z_)'], ['name', 'Name']]}
        selects={[['kind', 'Kind', ['recurring', 'variable', 'reimbursement']], ['country', 'Country', ['ALL', 'IN', 'UK', 'MY', 'GENERIC']]]}
        toggles={[['taxable', 'Taxable', true], ['one_time', 'One-time payment', true], ['active', 'Active', true]]}
        onSave={async (p) => { p.code = (p.code || '').toUpperCase(); if (p.id) await hr.patchEarningHead(p.id, p); else await hr.createEarningHead(p); }}
        onClose={() => setShowEdit(null)} onSaved={() => { setShowEdit(null); load(); }} />}
    </div>
  );
};

// ---- Generic master modal ----
const MasterModal = ({ title, initial, fields = [], selects = [], toggles = [], onSave, onClose, onSaved }) => {
  const [f, setF] = useState(() => {
    const base = { ...initial };
    toggles.forEach(([k, , dflt]) => { if (base[k] === undefined) base[k] = dflt || false; });
    selects.forEach(([k, , opts]) => { if (base[k] === undefined) base[k] = opts[0]; });
    return base;
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const p = { ...f };
      // number coercion
      fields.forEach(([k, , t]) => { if (t === 'number' && p[k] !== '' && p[k] != null) p[k] = parseFloat(p[k]); if (t === 'number' && (p[k] === '' || p[k] == null)) delete p[k]; });
      await onSave(p);
      toast({ title: 'Saved' });
      onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="master-modal">
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id ? 'Edit' : 'New'} {title}</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          {fields.map(([k, label, type = 'text']) => (
            <FormField key={k} label={label}>
              <Input type={type} value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`master-field-${k}`} />
            </FormField>
          ))}
          {selects.map(([k, label, opts]) => (
            <FormField key={k} label={label}>
              <Select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </Select>
            </FormField>
          ))}
          {toggles.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`master-toggle-${k}`} />
              {label}
            </label>
          ))}
          <button onClick={save} disabled={busy} data-testid="master-save-btn" className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">{busy && <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />}Save</button>
        </div>
      </div>
    </div>
  );
};

export default HRPage;
